import Commons from "oc-commons/Commons.js";
import Interval2 from "oc-commons/Interval2";

/**
 * Wkfg2TypeConverter wandelt clientseitig die Metadaten vom/für das Backend um.
 * Datentypen wie Vector3, Box3 usw. werden geparsed/formattiert (diese sind backendseitig nur Strings).
 */
export default class Wkfg2TypeConverter {

    /** */
    constructor() {
        this.parsers = Commons.getPrefixedMethods(this,'parse_');
        this.unmarshallers = Commons.getPrefixedMethods(this,'unmarshal_');
        this.formatters = Commons.getPrefixedMethods(this,'format_');
    }

    /**
     * Wandelt eine Rückgabe der WKFG2-Backend-API (Objekt mit Tabellennamen als Keys, Werte sind Arrays von Rows)
     * in das WKFG2 - Client - Format um, wobei die Objekte in-place verändert werden.
     */
    async parse(metadata,maybeSleepAtTable=null) {
        if (APP.metamodel==null) {
            throw new Error('metamodel not loaded');
        }
        for (const tableModel of APP.metamodel.tables) {
            const table = metadata[tableModel.name];
            if (table!=null) {
                if (maybeSleepAtTable) {
                    await maybeSleepAtTable(table);
                }
                this.parseTable(tableModel, table);
            }
        }
        return metadata;
    }

    /**
     * Wandelt spezifisch eine einzelne Tabelle (Array von Rows)
     * in das WKFG2 - Client - Format um, wobei die Objekte in-place verändert werden.
     */
    parseTable(tableModel, table) {
        tableModel.columns.forEach(columnModel=>{
            const isOne2Many = columnModel.multiselect || columnModel.variantKey;
            let typeParser = this.parsers[isOne2Many ? 'List' : (columnModel.jsType || columnModel.type)];
            if (typeParser!=null) {
                let typeParserOnlyStringInput = x => _.isString(x) ? typeParser(x,columnModel,tableModel) : x;
                const jsDefault = Commons.isEmpty(columnModel.jsDefault) ? null : typeParserOnlyStringInput(columnModel.jsDefault);
                table.forEach(row=>{
                    const rowValue = row[columnModel.name];
                    row[columnModel.name] = Commons.isEmpty(rowValue) ? jsDefault : typeParserOnlyStringInput(rowValue);
                });
            }
            const unmarshaller = this.unmarshallers[columnModel.jsType || columnModel.type];
            if (unmarshaller!=null) {
                table.forEach(row=>{
                    const rowValue = row[columnModel.name];
                    row[columnModel.name] = rowValue==null ? null : unmarshaller(rowValue,columnModel,tableModel);
                });
            }
        });
    }

    /**
     * Wandelt Settings in das WKFG2 - Client - Format um, wobei die Objekte in-place verändert werden.
     */
    parseSettings(settings,settingsMeta) {
        settingsMeta.forEach(meta=>{
            const value = this.parseSingleSetting(meta.Value,meta);
            _.set(settings, meta.Key, value);
        });
    }

    /** */
    parseSingleSetting(value,meta) {
        if (!_.isString(value)) {
            return value;
        }
        let typeParser = this.parsers[meta.Type];
        return typeParser==null ? value : typeParser(value,meta);
    }

    /**
     * Erstellt eine Kopie eines Datensatz, wandelt diese in das WKFG2 - Backend - Format um und gibt sie zurück.
     * @param {string} tableName
     * @param {?Object} row
     * @returns {?Object}
     */
    format(tableName, row) {
        if (row==null) {
            return null;
        }
        if (APP.metamodel==null) {
            throw new Error('metamodel not loaded');
        }
        const result = Commons.cloneDeep(row);
        const tableModel = APP.metamodel.tables.find(table=>table.name==tableName);
        tableModel.columns.forEach(columnModel=>{
            const typeName = columnModel.jsType || columnModel.type;
            let typeFormatter = this.formatters[typeName];
            if (typeFormatter!=null) {
                const rowValue = result[columnModel.name];
                if (rowValue==null) {
                    let typeParser = this.parsers[typeName] || (x=>x);
                    result[columnModel.name] = Commons.isEmpty(columnModel.jsDefault) ? null : typeFormatter(typeParser(columnModel.jsDefault),columnModel);
                } else {
                    result[columnModel.name] = typeFormatter(rowValue,columnModel);
                }
            }
        });
        return result;
    }

    /**
     * Erstellt eine leere mit default Werten gefüllte Instance einer Zeile basierend auf den TableDaten
     * @param {string} tableName
     * @returns {?Object}
     */
    createInstance(tableName){
        if (APP.metamodel==null) {
            throw new Error('metamodel not loaded');
        }
        const tableModel = APP.metamodel.tables.find(table=>table.name==tableName);
        if (tableModel!=null){
            const result={};
            tableModel.columns.forEach(columnModel=>{
                const typeParser = this.parsers[columnModel.jsType || columnModel.type];
                let jsDefault = Commons.isEmpty(columnModel.jsDefault) ? null : columnModel.jsDefault;
                if (typeParser!=null) {
                    jsDefault = Commons.isEmpty(columnModel.jsDefault) ? null : typeParser(columnModel.jsDefault);
                }
                result[columnModel.name] = jsDefault;
            });
            return result;
        }
        else{
            return null;
        }
    }

    parseFloat(s) {
        if (s==null) {
            return 0;
        }
        // das hier ist die JS-Standardfunktion:
        return parseFloat(s);
    }

    ///////////////////////////////////////////////////////////////////
    // interne konkrete Type-Parser
    // Der reingereichte Wert ist immer ein String und niemals null oder empty,
    // das nimmt schon die Methode transformTypes vorweg,
    // und auch jsDefault
    ///////////////////////////////////////////////////////////////////

    /**
     * wird nur für Settings und nicht für andere Metamodell-Tabellen verwendet, weil primitive Werte bereits serverseitig konvertiert werden
     */
    parse_Float(v) {
        return this.parseFloat(v);
    }

    /**
     * wird nur für Settings und nicht für andere Metamodell-Tabellen verwendet, weil primitive Werte bereits serverseitig konvertiert werden
     */
    parse_Int(v) {
        return v==null ? 0 : parseInt(v,10);
    }

    /**
     * wird nur für Settings und nicht für andere Metamodell-Tabellen verwendet, weil primitive Werte bereits serverseitig konvertiert werden
     */
    parse_Bool(v) {
        return v===true || v==='true';
    }

    /**
     * eine beliebig lange Liste von Zahlen
     */
    parse_Vector(v) {
        return v.split(/\s*,\s*/).map(x=>parseFloat(x));
    }

    /** */
    parse_Vector2(v) {
        const p = v.split(/\s*,\s*/);
        const o = new THREE.Vector2();
        o.x = this.parseFloat(p[0]);
        o.y = this.parseFloat(p[1]);
        return o;
    }

    /** */
    parse_Vector3(v) {
        const p = v.split(/\s*,\s*/);
        const o = new THREE.Vector3();
        o.x = this.parseFloat(p[0]);
        o.y = this.parseFloat(p[1]);
        o.z = this.parseFloat(p[2]);
        return o;
    }

    /** */
    parse_Vector4(v) {
        const p = v.split(/\s*,\s*/);
        const o = new THREE.Vector4();
        o.x = this.parseFloat(p[0]);
        o.y = this.parseFloat(p[1]);
        o.z = this.parseFloat(p[2]);
        o.w = this.parseFloat(p[3]);
        return o;
    }

    /** */
    parse_Box3(v) {
        const p = v.split(/\s*,\s*/);
        const a = new THREE.Vector3();
        a.x = this.parseFloat(p[0]);
        a.y = this.parseFloat(p[1]);
        a.z = this.parseFloat(p[2]);
        const b = new THREE.Vector3();
        b.x = this.parseFloat(p[3]);
        b.y = this.parseFloat(p[4]);
        b.z = this.parseFloat(p[5]);
        return new THREE.Box3(a,b);
    }

    /** */
    parse_Map(v) {
        const map = {};
        if (v.length>0) {
            let pairs;
            try {
                //https://caniuse.com/js-regexp-lookbehind
                const allowEscapedColons = new RegExp("\\s*(?<!\\\\),\\s*","g");
                pairs = v.split(allowEscapedColons);
            }
            catch (e){
                //https://caniuse.com/?search=look%20ah
                const reverse = (st)=>st.split('').reverse().join('');
                const allowEscapedColons = new RegExp("\\s*,(?!\\\\)\\s*","g");
                pairs=reverse(v).split(allowEscapedColons)?.map(x=>reverse(x));
            }
            pairs?.map(p => p.replace(/\\,/g, ',').split(/\s*=\s*/)).forEach(([key, value]) => {
                if (key && value) {
                    map[key] = value
                }
            });
        }
        return map;
    }

    /** */
    parse_xMap(v) {
        const map = {};
        v.split(/\s*,\s*/).map(p=>p.split(/\s*x\s*/)).forEach(([key,value])=>map[value]=this.parseFloat(key));
        return map;
    }

    /** */
    parse_Rotation(v) {
        const r = this.parse_Vector3(v);
        r.multiplyScalar(Math.PI/180);
        return new THREE.Quaternion().setFromEuler(new THREE.Euler(r.x,r.y,r.z));
    }

    /** */
    parse_List(v) {
        return v.split(/\s*,\s*/).filter(x=>x.length);
    }

    /** */
    parse_Color(v) {
        return new THREE.Color(v);
    }

    /** */
    parse_NumberOrInterval(v) {
        return Interval2.parse(v);
    }

    /** */
    parse_Image(v,columnMeta) {
        return this._getContentUrl(v,columnMeta);
    }

    /** */
    parse_Model3D(v,columnMeta) {
        return this._getContentUrl(v,columnMeta);
    }

    _getContentUrl(v,columnMeta) {
        if (APP.metamodel.version>=2) {
            return APP.getContentUrl(columnMeta,v);
        }
        return v;
    }

    ///////////////////////////////////////////////////////////////////
    // interne konkrete Type-Formatter
    // Der reingereichte Wert ist niemals null.
    ///////////////////////////////////////////////////////////////////


    /**
     * wird nur für Settings und nicht für andere Metamodell-Tabellen verwendet, weil primitive Werte bereits serverseitig konvertiert werden
     */
    format_Float(v) {
        return v.toString();
    }

    /**
     * wird nur für Settings und nicht für andere Metamodell-Tabellen verwendet, weil primitive Werte bereits serverseitig konvertiert werden
     */
    format_Int(v) {
        return v.toString();
    }

    /**
     * wird nur für Settings und nicht für andere Metamodell-Tabellen verwendet, weil primitive Werte bereits serverseitig konvertiert werden
     */
    format_Bool(v) {
        return v.toString();
    }

    /**
     * eine beliebig lange Liste von Zahlen
     */
    format_Vector(v) {
        return v.join(',');
    }

    /** */
    format_Vector2(v) {
        return v.toArray().join(',');
    }

    /** */
    format_Vector3(v) {
        return v.toArray().join(',');
    }

    /** */
    format_Vector4(v) {
        return v.toArray().join(',');
    }

    /** */
    format_Box3(v) {
        return this.format_Vector3(v.min)+','+this.format_Vector3(v.max);
    }

    /** */
    format_Map(v) {
        return _.toPairs(v).map(([k,v])=>k+'='+v).join(',');
    }

    /** */
    format_xMap(v) {
        return _.toPairs(v).map(([k,v])=>v+'x'+k).join(',');
    }

    /** */
    format_Rotation(v) {
        return this.format_Vector3(new THREE.Euler().setFromQuaternion(v,'XYZ').toVector3().multiplyScalar(180/Math.PI*1000000).round().multiplyScalar(1/1000000));
    }

    /** */
    format_ModelData(v) {
        const json = Commons.cloneDeep(v);
        json.boundingBox = json.boundingBox==null ? null : json.boundingBox.toJSON();
        json.boundingBoxUntransformed = json.boundingBoxUntransformed==null ? null : json.boundingBoxUntransformed.toJSON();
        return json;
    }

    format_ConnectorList(v) {
        const json = Commons.cloneDeep(v);
        json.forEach(config => {
            config.position_absolut = this.format_Vector3(config.position_absolut);
            config.position_relativ = this.format_Vector3(config.position_relativ);
            config.rotation = this.format_Rotation(config.rotation);
        });
        return json;
    }

    /** */
    format_List(v) {
        return v.join(',');
    }

    /** */
    format_Color(v) {
        if (v.getHexString)
            return '#'+v.getHexString();
        else if (typeof v == "number")
            return "#" + v.toString(16);
        else if (v.startsWith("#"))
            return v;
        else
            return '#' + v;
    }

    /** */
    format_NumberOrInterval(v) {
        return ''+v;
    }

    /** */
    format_Image(v,columnMeta) {
        return this._formatContentUrl(v,columnMeta);
    }

    /** */
    format_Model3D(v,columnMeta) {
        return this._formatContentUrl(v,columnMeta);
    }

    _formatContentUrl(v,columnMeta) {
        if (APP.metamodel.version>=2 && APP.formatContentValue) {
            return APP.formatContentValue(columnMeta,v);
        }
        return v;
    }

    ///////////////////////////////////////////////////////////////////
    // interne konkrete Type-Unmarshaller
    // Der reingereichte Wert ist niemals null.
    // Im Gegensatz zum Parsen bedeutet Unmarshal, dass auch nicht-String-
    // Werte verarbeitet werden können, also z.B. JSON
    ///////////////////////////////////////////////////////////////////

    /** */
    unmarshal_ModelData(v) {
        if (_.isString(v)) {
            v = JSON.parse(v);
        }
        const json = Commons.cloneDeep(v);
        if (json.boundingBox) {
            json.boundingBox = new THREE.Box3().fromJSON(json.boundingBox);
        }
        if (json.boundingBoxUntransformed) {
            json.boundingBoxUntransformed = new THREE.Box3().fromJSON(json.boundingBoxUntransformed);
        }
        return json;
    }

    unmarshal_ConnectorList(v) {
        if (Commons.isEmpty(v)) {
            return [];
        }
        if (_.isString(v)) {
            v = JSON.parse(v);
        }
        const json = Commons.cloneDeep(v);
        json.forEach(config => {
            config.position_absolut = this.parse_Vector3(config.position_absolut);
            config.position_relativ = this.parse_Vector3(config.position_relativ);
            config.rotation = this.parse_Rotation(config.rotation);
        });
        return json;


    }

}
